import { useQuery } from "@tanstack/react-query";
import { Activity, Clock3, FileSignature, ShieldCheck, Workflow as WorkflowIcon } from "lucide-react";
import { useApi, type CaseEvent, type WorkflowInstanceView } from "@ncp/api-client";
import { formatDateTime, useLocale, useTranslation } from "@ncp/i18n";
import { CollapsibleSection, Timeline, SourceText } from "@ncp/ui";
import type { SlaClockDto } from "../api";
import { ReviewHistory } from "../review/ReviewHistory";
import { AgentsTab } from "./AgentsTab";
import { AuditTab } from "./AuditTab";
import { WorkflowTab } from "./WorkflowTab";
import { useActorName, useStaffName } from "../../../lib/staff-names";

export type HistoryView = "timeline" | "reviews" | "process" | "agents" | "audit";

/** Everything that happened on the case, in one place: the journal, the stage reviews signed off, the process and its clocks, the agent runs and the audit chain. */
export function HistoryTab({ caseId, workflow, clocks, view, onViewChange }: { caseId: string; caseNumber?: string; workflow?: WorkflowInstanceView; clocks: SlaClockDto[]; view: HistoryView | null; onViewChange: (v: HistoryView | null) => void }) {
  const { t } = useTranslation();
  const staffName = useStaffName();
  const actorName = useActorName();
  const { locale } = useLocale();
  const api = useApi();
  const events = useQuery({ queryKey: ["case-events", caseId], queryFn: () => api.get<CaseEvent[]>(`/api/cases/${caseId}/workflow/events`) });
  const section = (key: HistoryView, icon: typeof Clock3, title: string, children: React.ReactNode, actions?: React.ReactNode) => {
    const Icon = icon;
    return (
      <CollapsibleSection key={key} title={<span className="flex items-center gap-2"><Icon className="size-4 text-aegold-700" /> {title}</span>} actions={actions} open={view === key} onOpenChange={(o) => onViewChange(o ? key : null)}>
        {children}
      </CollapsibleSection>
    );
  };
  return (
    <div className="space-y-3">
      {section("timeline", Clock3, t("backoffice:workspace.journal"), (
        // Entries recorded only in English read through the shared content layer (Arabic terms, marked translation) on an Arabic screen.
        <Timeline items={(events.data ?? []).map((e) => {
          const detail = staffName(locale === "ar" ? e.detailAr ?? e.detail : e.detail);
          return { id: e.id, title: <SourceText text={staffName(locale === "ar" && e.titleAr ? e.titleAr : e.title)} />, detail: detail ? <SourceText text={detail} /> : undefined, time: formatDateTime(e.occurredAt, locale), kind: e.kind === "Transition" || e.kind === "Decision" || e.kind === "ReviewConcluded" ? "milestone" : e.actorKind === "Agent" ? "agent" : e.actorKind === "System" ? "system" : "human", actor: <SourceText text={actorName(e)} /> };
        })} />
      ))}
      {section("reviews", FileSignature, t("backoffice:review.stageReviews"), <ReviewHistory caseId={caseId} />)}
      {section("process", WorkflowIcon, t("backoffice:workspace.procedureGuide"), <WorkflowTab caseId={caseId} workflow={workflow} clocks={clocks} />)}
      {section("agents", Activity, t("backoffice:workspace.agents"), <AgentsTab caseId={caseId} />)}
      {section("audit", ShieldCheck, t("backoffice:workspace.audit"), <AuditTab caseId={caseId} />)}
    </div>
  );
}
